// packages/charts/src/react/FunnelExplorerChart.tsx
//
// GA4-style funnel explorer for reports (plan
// 2026-08-27-report-funnel-explorer-block): step columns with a bar +
// drop-off wedge, per-step conversion in the header, abandonment under
// each step, a breakdown table below, and a per-day detail panel for the
// selected step on the right.
//
// Rendering: the funnel itself is HTML + per-column SVG wedges —
// deliberately the first non-ECharts visual in this package. The GA4
// shape (straight bars, sloped drop-off fills, dashed column rules,
// comparison ticks) styles and reflows more precisely as markup than as
// an ECharts custom series, and markup survives the email capture the
// same way the SVG renderer does. Only the detail panel is ECharts.
//
// The string "funnel_explorer" also names a chat/dashboard chart type
// with a different payload shape — same concept, two pipelines, no
// shared consumer (see types.ts).
//
// Theme tokens consumed:
//   --verity-chart-funnel-palette-1 (bar + wedge tint + swatches)
//   --verity-chart-bar-fill / --verity-color-positive (detail panel)
//   --verity-color-{text,text-muted,border,accent-soft,surface-muted}
//
// Interaction: each step is a real <button> (Enter/Space select,
// aria-pressed) — selection re-points the detail panel and highlights
// the table column. Static mode (email SVG render) drops the detail
// panel and all affordances; the funnel + table stand alone.
import * as React from "react";
import { useChartRenderer } from "./ChartRendererContext";
import { EmptyChart } from "./EmptyChart";
import {
  tooltipWithMeta,
  valueAxisLabel,
  withBaseOptions,
  AXIS_LABEL_DEFAULT,
} from "./baseOptions";
import { formatChartValue } from "./formatValue";
import { resolveCssVar } from "./resolveCssVars";
import {
  buildFunnelDetailModel,
  buildFunnelExplorerModel,
  formatOrDash,
  type FunnelStepModel,
} from "./funnelExplorerModel";
import type { ChartMeta, FunnelExplorerData } from "./types";

export interface FunnelExplorerChartProps {
  data: FunnelExplorerData;
  meta?: ChartMeta;
  ariaLabel?: string;
  /** Legend label for the comparison period ("Previous period" /
   * "Last year"). Shown in the table's vs. rows and the funnel ticks. */
  comparisonName?: string;
  /** Force static rendering (no selection, no detail panel). Defaults to
   * the email runtime's SVG flag so the static edition needs no wiring. */
  interactive?: boolean;
  /** Dashboard hosts only: click a breakdown row name to filter the tab
   * on that value (see BreakdownTable). Ignored when static. */
  onSelectBreakdownValue?: (name: string) => void;
  /** The active breakdown-value filter, for row styling. */
  activeBreakdownValue?: string | null;
  /** A report's chart palette (literal colours). Its first entry replaces
   * the `--verity-chart-funnel-palette-1` fill and the detail panel's bar
   * colour. Absent → the tokens, unchanged. */
  palette?: string[];
}

const CHART_HEIGHT = 220;
const BAR_SHARE = 0.55; // bar width as share of the column; wedge gets the rest

const FUNNEL_FILL = "var(--verity-chart-funnel-palette-1, #3455D3)";
// The fill every part of the strip paints with: the token by default, a
// palette's first colour when the host passed one. A context, not a prop
// through every sub-component, because four of them read it.
const FunnelFillContext = React.createContext<string>(FUNNEL_FILL);
// The detail panel's bar: `null` = resolve the token, as before.
const DetailBarContext = React.createContext<string | null>(null);
const TEXT = "var(--verity-color-text, #111827)";
const TEXT_MUTED = "var(--verity-color-text-muted, #6b7280)";
const BORDER = "var(--verity-color-border, #e5e7eb)";
const ACCENT_SOFT = "var(--verity-color-accent-soft, #EEF2FC)";
// Step separators: thin dashed hairlines running header → chart →
// abandonment. The regular border token — deliberately faint (Arjen:
// "veel lichter"); they mark columns, they don't frame them.
const STEP_RULE = `1px dashed ${BORDER}`;

function fmtPct(v: number | null, decimals = 1): string {
  return formatOrDash(v, (n) => `${n.toFixed(decimals)}%`);
}

function fmtCount(v: number | null, meta?: ChartMeta): string {
  return formatOrDash(v, (n) => formatChartValue(n, meta, "full"));
}

/** Bar height as a fraction of the chart area (0..1). */
function heightFrac(value: number | null, maxValue: number): number {
  if (value == null || maxValue <= 0) return 0;
  return Math.max(0, Math.min(1, value / maxValue));
}

// ---------------------------------------------------------------------------
// Funnel strip
// ---------------------------------------------------------------------------

type HoverSeries = "actual" | "comparison" | null;

function StepColumn({
  step,
  nextStep,
  index,
  maxValue,
  meta,
  selected,
  dimmed,
  interactive,
  comparisonName,
  onSelect,
  hoverSeries,
  onHoverSeries,
  onHoverStep,
  headerHeight,
}: {
  step: FunnelStepModel;
  nextStep: FunnelStepModel | null;
  index: number;
  maxValue: number;
  meta?: ChartMeta;
  selected: boolean;
  dimmed: boolean;
  interactive: boolean;
  comparisonName?: string;
  onSelect: (key: string) => void;
  hoverSeries: HoverSeries;
  onHoverSeries: (s: HoverSeries) => void;
  onHoverStep: (key: string | null) => void;
  headerHeight: number;
}): React.ReactElement {
  const funnelFill = React.useContext(FunnelFillContext);
  const frac = heightFrac(step.value, maxValue);
  // No drop-off wedge into a missing step: sloping to zero would draw the
  // "everyone left" story the data cannot support.
  const nextFrac =
    nextStep && !nextStep.missing ? heightFrac(nextStep.value, maxValue) : null;
  const cmpFrac =
    step.comparisonValue != null ? heightFrac(step.comparisonValue, maxValue) : null;
  const nextCmpFrac =
    nextStep && !nextStep.missing && nextStep.comparisonValue != null
      ? heightFrac(nextStep.comparisonValue, maxValue)
      : null;
  const barTopPct = (1 - frac) * 100;

  // The bar group sits CENTRED in its column (Arjen, ronde 9). With a
  // comparison the zone splits into two side-by-side bars: actual (funnel
  // fill) + comparison (neutral grey — a lighter blue melts into the
  // drop-off wedge). Without one the actual bar takes the whole zone.
  // Each wedge runs BEHIND the bars from its own bar's right edge across
  // the column boundary to the NEXT column's same-series bar; the overflow
  // stays behind the neighbour's bars because columns paint in DOM order.
  const zonePct = BAR_SHARE * 100;
  const hasCmpBar = cmpFrac != null;
  const barWidthPct = hasCmpBar ? zonePct * 0.46 : zonePct;
  const padPct = (100 - zonePct) / 2;
  const actualLeftPct = padPct;
  const cmpLeftPct = padPct + zonePct * 0.54;

  // Step-to-step CTR labels, shown in the drop-off zone between bars.
  // The actual CTR is the NEXT step's conversion-from-previous; the
  // comparison CTR is derived here (the model keeps per-step numbers).
  const ctr =
    nextStep && !nextStep.missing && !step.missing ? nextStep.conversionPct : null;
  const cmpCtr =
    nextStep &&
    step.comparisonValue != null &&
    step.comparisonValue > 0 &&
    nextStep.comparisonValue != null
      ? (nextStep.comparisonValue / step.comparisonValue) * 100
      : null;

  // Series hover: the hovered series stays crisp, the other recedes —
  // and the comparison PROMOTES itself on hover (its resting grey is
  // deliberately quiet, so emphasis needs a darker fill, not just a
  // dimmed neighbour). Purely decorative — no information lives only
  // in it.
  const cmpEmphasis = hoverSeries === "comparison";
  const actualDim = cmpEmphasis ? 0.3 : 1;
  const cmpDim = hoverSeries === "actual" ? 0.3 : 1;
  // Resting grey dark enough to read as a real series (Arjen, ronde 6);
  // emphasis steps two shades further so hover still visibly promotes.
  const cmpFill = cmpEmphasis
    ? "var(--verity-gray-6, #4B5563)"
    : "var(--verity-gray-4, #9CA3AF)";
  const FADE = "opacity 130ms ease, background 130ms ease";
  const hoverActual = interactive
    ? {
        onMouseEnter: () => onHoverSeries("actual"),
        onMouseLeave: () => onHoverSeries(null),
      }
    : {};
  const hoverCmp = interactive
    ? {
        onMouseEnter: () => onHoverSeries("comparison"),
        onMouseLeave: () => onHoverSeries(null),
      }
    : {};

  // Header shows the CUMULATIVE share of step 1 reaching this step — the
  // last step therefore reads as the funnel's total conversion. The
  // step-to-step CTR lives in the chips between the bars.
  const headerPct = step.isFirst
    ? "100%"
    : fmtPct(
        step.completionPct,
        step.completionPct != null && step.completionPct >= 100 ? 0 : 1,
      );

  const graphOpacity = dimmed ? 0.4 : 1;

  const body = (
    <>
      {/* Header: Step n / name / cumulative share of step 1 (+ comparison) */}
      <div style={{ padding: "0 12px 8px 12px", minHeight: headerHeight }}>
        <div style={{ fontSize: "var(--verity-font-size-xs, 12px)", color: TEXT_MUTED }}>
          Step {index + 1}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: "var(--verity-font-size-sm, 13px)",
              fontWeight: 600,
              color: TEXT,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={step.label}
          >
            {step.label}
          </span>
          <span
            style={{
              fontSize: "var(--verity-font-size-sm, 13px)",
              color: TEXT,
              fontVariantNumeric: "tabular-nums",
              flexShrink: 0,
            }}
          >
            {step.missing ? "—" : headerPct}
          </span>
        </div>
        {/* Delta only — the comparison's own cumulative % added noise
         * (Arjen, ronde 11); the full number lives in the tooltip/table. */}
        {!step.isFirst &&
          step.completionPct != null &&
          step.comparisonCompletionPct != null && (
            <div
              style={{
                textAlign: "right",
                fontSize: 11,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                color:
                  step.completionPct - step.comparisonCompletionPct >= 0
                    ? "var(--verity-color-positive, #0d9488)"
                    : "var(--verity-color-negative, #f03e3e)",
              }}
            >
              {step.completionPct - step.comparisonCompletionPct >= 0 ? "↑" : "↓"}{" "}
              {Math.abs(step.completionPct - step.comparisonCompletionPct).toFixed(1)}pt
            </div>
          )}
      </div>

      {/* Chart cell: bar + drop-off wedge + comparison tick */}
      <div
        style={{ position: "relative", height: CHART_HEIGHT, opacity: graphOpacity }}
        title={
          step.missing
            ? `${step.label}: no data`
            : `${step.label}: ${fmtCount(step.value, meta)}` +
              (step.comparisonValue != null && comparisonName
                ? ` · ${comparisonName}: ${fmtCount(step.comparisonValue, meta)}`
                : "")
        }
      >
        {step.missing ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: TEXT_MUTED,
              fontSize: "var(--verity-font-size-sm, 13px)",
            }}
          >
            —
          </div>
        ) : (
          <>
            {/* Comparison drop-off wedge: same story in grey, running BAR TO
             * BAR — from this comparison bar's right edge across the column
             * boundary to the NEXT column's comparison bar. The overflow
             * into the neighbour column stays behind its bars because
             * columns paint in DOM order and the wedge sits at z 0.
             * Only visible WHILE the comparison series is hovered (resting
             * state stays clean); pointer-events off so the invisible wedge
             * can't summon itself over empty space. Drawn FIRST so the blue
             * actual wedge reads on top. */}
            {hasCmpBar && nextCmpFrac != null && (
              <svg
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: `${padPct + zonePct}%`,
                  bottom: 0,
                  width: `${100 - barWidthPct}%`,
                  height: "100%",
                  zIndex: 0,
                  opacity: cmpEmphasis ? 1 : 0,
                  transition: FADE,
                  pointerEvents: "none",
                }}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <polygon
                  points={`0,${(1 - (cmpFrac ?? 0)) * 100} 100,${(1 - nextCmpFrac) * 100} 100,100 0,100`}
                  fill={cmpFill}
                  fillOpacity={0.35}
                />
              </svg>
            )}
            {nextFrac != null && (
              <svg
                aria-hidden="true"
                {...hoverActual}
                style={{
                  position: "absolute",
                  left: `${actualLeftPct + barWidthPct}%`,
                  bottom: 0,
                  width: `${100 - barWidthPct}%`,
                  height: "100%",
                  zIndex: 0,
                  opacity: actualDim,
                  transition: FADE,
                }}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <polygon
                  points={`0,${(1 - frac) * 100} 100,${(1 - nextFrac) * 100} 100,100 0,100`}
                  fill={funnelFill}
                  fillOpacity={0.28}
                />
              </svg>
            )}
            <div
              {...hoverActual}
              // A step's bar: what a static edition counts as drawn
              // (`report-components/src/static/drawnMarker.ts`).
              data-funnel-bar=""
              style={{
                position: "absolute",
                left: `${actualLeftPct}%`,
                bottom: 0,
                width: `${barWidthPct}%`,
                height: `${frac * 100}%`,
                background: funnelFill,
                zIndex: 1,
                opacity: actualDim,
                transition: FADE,
              }}
            />
            {hasCmpBar && (
              <div
                {...hoverCmp}
                style={{
                  position: "absolute",
                  left: `${cmpLeftPct}%`,
                  bottom: 0,
                  width: `${barWidthPct}%`,
                  height: `${(cmpFrac ?? 0) * 100}%`,
                  background: cmpFill,
                  zIndex: 1,
                  opacity: cmpDim,
                  transition: FADE,
                }}
              />
            )}
            {/* Count labels: above each bar, or tucked inside the bar top
             * when it is (near) full height so they never collide with the
             * step header. Comparison label is muted, actual leads. */}
            <div
              style={{
                position: "absolute",
                left: `${actualLeftPct}%`,
                width: `${barWidthPct}%`,
                top:
                  frac > 0.86
                    ? `calc(${barTopPct}% + 6px)`
                    : `calc(${barTopPct}% - 20px)`,
                textAlign: "left",
                paddingLeft: frac > 0.86 ? 6 : 2,
                fontSize: "var(--verity-font-size-xs, 12px)",
                fontWeight: 600,
                color: frac > 0.86 ? "rgba(255,255,255,0.95)" : TEXT,
                fontVariantNumeric: "tabular-nums",
                overflow: "visible",
                whiteSpace: "nowrap",
                zIndex: 2,
                opacity: actualDim,
                transition: FADE,
              }}
            >
              {formatChartValue(step.value, meta, "axis")}
            </div>
            {hasCmpBar && (
              <div
                style={{
                  position: "absolute",
                  left: `${cmpLeftPct}%`,
                  width: `${barWidthPct}%`,
                  top:
                    (cmpFrac ?? 0) > 0.86
                      ? `calc(${(1 - (cmpFrac ?? 0)) * 100}% + 6px)`
                      : `calc(${(1 - (cmpFrac ?? 0)) * 100}% - 18px)`,
                  textAlign: "left",
                  paddingLeft: (cmpFrac ?? 0) > 0.86 ? 6 : 2,
                  fontSize: 11,
                  fontWeight: cmpEmphasis ? 600 : 500,
                  // Inside the resting light-grey bar: dark ink; inside the
                  // emphasised darker bar: white; above the bar: muted,
                  // promoting to full text ink on emphasis.
                  color:
                    (cmpFrac ?? 0) > 0.86
                      ? cmpEmphasis
                        ? "rgba(255,255,255,0.95)"
                        : "rgba(31,41,55,0.85)"
                      : cmpEmphasis
                        ? TEXT
                        : TEXT_MUTED,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                  zIndex: 2,
                  opacity: cmpDim,
                  transition: FADE,
                }}
              >
                {formatChartValue(step.comparisonValue, meta, "axis")}
              </div>
            )}
            {/* ONE step-CTR chip per transition: actual on top, comparison
             * + delta (percentage POINTS — the honest delta between two
             * rates) beneath, mirroring the table-cell pattern. Carries
             * both series, so it never dims with either; it just rides
             * above the actual slope's midpoint, bottom-anchored via
             * translateY so a taller chip grows upward. */}
            {ctr != null && nextFrac != null && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  // In the open gap RIGHT of the (comparison) bar zone, so
                  // the chip clears both series' count labels; vertically
                  // it rides the actual slope at its own x-position.
                  left: `${padPct + zonePct}%`,
                  width: `${100 - zonePct}%`,
                  top: `calc(${(1 -
                    (frac +
                      (((zonePct + 100) / 2 - barWidthPct) / (100 - barWidthPct)) *
                        (nextFrac - frac))) * 100}% - 6px)`,
                  display: "flex",
                  justifyContent: "center",
                  pointerEvents: "none",
                  zIndex: 2,
                  transform: "translateY(-100%)",
                }}
              >
                <span
                  style={{
                    background: "var(--verity-color-surface, #fff)",
                    border: `1px solid ${BORDER}`,
                    borderRadius: "var(--verity-radius-md, 8px)",
                    padding: "2px 8px",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    lineHeight: 1.35,
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      fontSize: "var(--verity-font-size-xs, 12px)",
                      fontWeight: 600,
                      color: TEXT,
                    }}
                  >
                    → {ctr.toFixed(1)}%
                  </span>
                  {cmpCtr != null && (
                    <span style={{ display: "block", fontSize: 11 }}>
                      <span style={{ color: TEXT_MUTED }}>
                        vs {cmpCtr.toFixed(1)}%{" "}
                      </span>
                      <span
                        style={{
                          color:
                            ctr - cmpCtr >= 0
                              ? "var(--verity-color-positive, #0d9488)"
                              : "var(--verity-color-negative, #f03e3e)",
                        }}
                      >
                        {ctr - cmpCtr >= 0 ? "↑" : "↓"}{" "}
                        {Math.abs(ctr - cmpCtr).toFixed(1)}pt
                      </span>
                    </span>
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const columnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    // Step separators are drawn as one continuous overlay in the parent
    // wrapper — no per-column borders (they left seams at the strip joint).
    paddingTop: 12,
  };

  if (!interactive) {
    return <div style={columnStyle}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(step.key)}
      onMouseEnter={() => onHoverStep(step.key)}
      onMouseLeave={() => onHoverStep(null)}
      aria-pressed={selected}
      aria-label={`Select step ${index + 1}: ${step.label}`}
      style={{
        ...columnStyle,
        display: "block",
        width: "100%",
        textAlign: "left",
        // The lane-hover wash is drawn by the wrapper overlay (it spans
        // the abandonment row too) — the button stays transparent.
        background: "none",
        // Longhand only — mixing `border` shorthand with `borderLeft`
        // trips React's conflicting-style warning on every rerender.
        border: "none",
        margin: 0,
        padding: 0,
        paddingTop: 12,
        cursor: "pointer",
        font: "inherit",
      }}
    >
      {body}
    </button>
  );
}

function AbandonmentCell({
  step,
  meta,
  onHoverStep,
}: {
  step: FunnelStepModel;
  meta?: ChartMeta;
  /** Present in interactive mode: this row participates in the lane
   * hover, so sweeping over it lights the same column band. */
  onHoverStep?: (key: string | null) => void;
}): React.ReactElement {
  const funnelFill = React.useContext(FunnelFillContext);
  const hoverProps = onHoverStep
    ? {
        onMouseEnter: () => onHoverStep(step.key),
        onMouseLeave: () => onHoverStep(null),
      }
    : {};
  // The last step has no abandonment, but its (empty) cell still carries
  // the top rule so the horizontal line runs the full strip width.
  if (step.isLast) {
    return (
      <div
        {...hoverProps}
        style={{ flex: 1, minWidth: 0, borderTop: `1px solid ${BORDER}` }}
      />
    );
  }
  return (
    <div
      {...hoverProps}
      style={{
        flex: 1,
        minWidth: 0,
        padding: "10px 12px",
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      <div style={{ fontSize: "var(--verity-font-size-xs, 12px)", color: TEXT_MUTED }}>
        Abandonment rate
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginTop: 4,
          fontSize: "var(--verity-font-size-sm, 13px)",
          color: TEXT,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: funnelFill,
            opacity: 0.5,
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600 }}>
          {fmtCount(step.abandonmentCount, meta)}
        </span>
        <span style={{ color: TEXT_MUTED }}>{fmtPct(step.abandonmentPct)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown table
// ---------------------------------------------------------------------------

function BreakdownTable({
  model,
  meta,
  selectedKey,
  onSelectStep,
  onHoverStep,
  onSelectBreakdownValue,
  activeBreakdownValue,
}: {
  model: ReturnType<typeof buildFunnelExplorerModel>;
  meta?: ChartMeta;
  selectedKey: string | null;
  /** Click a step column (header or any cell) to select that step — the
   * same selection the funnel columns drive (Arjen, 28-08). Absent in
   * static/email rendering. */
  onSelectStep?: (key: string) => void;
  /** Hover previews the selection, mirroring the funnel columns. */
  onHoverStep?: (key: string | null) => void;
  /** Click a breakdown ROW NAME (the sticky first column — not the data
   * cells, those carry the step-column click) to filter on that value;
   * the host wires this to the dashboard's tab filter. "(not set)" and
   * the Total row never fire it (a bucket label is not a filterable
   * value). Absent outside dashboards (reports/chat). */
  onSelectBreakdownValue?: (name: string) => void;
  /** The currently active breakdown-value filter, for row styling. */
  activeBreakdownValue?: string | null;
}): React.ReactElement | null {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  // True while the pointer is inside this table: a selection change that
  // ORIGINATES here must not auto-scroll the table under the cursor —
  // hovering a half-visible column would shift the very column being
  // hovered and flicker the selection.
  const pointerInTableRef = React.useRef(false);
  // Keep the highlighted step column in view: hover/click on a step whose
  // column sits outside the table's horizontal scroll window scrolls it in.
  React.useEffect(() => {
    const container = scrollRef.current;
    if (!container || !selectedKey) return;
    if (pointerInTableRef.current) return;
    const el = container.querySelector<HTMLElement>(
      `th[data-step-key="${CSS.escape(selectedKey)}"]`,
    );
    if (!el) return;
    // Manual math instead of scrollIntoView — that can also scroll the
    // PAGE vertically, which would yank the report while hovering.
    const cLeft = container.scrollLeft;
    const cRight = cLeft + container.clientWidth;
    const eLeft = el.offsetLeft;
    const eRight = eLeft + el.offsetWidth;
    if (eRight > cRight) {
      container.scrollTo({ left: eRight - container.clientWidth, behavior: "smooth" });
    } else if (eLeft < cLeft) {
      container.scrollTo({ left: eLeft, behavior: "smooth" });
    }
  }, [selectedKey]);

  // Value-mode toggle (Arjen, 28-08): "abs" = raw counts; "pct" = each
  // cell as a share of the row's OWN step-1 value (the cumulative
  // percentages, matching the funnel headers' top row); "ctr" = the
  // step-to-step conversion per cell (cell i / cell i-1 of the same row,
  // matching the → chips between the funnel columns; step 1 has no
  // previous step and shows "—"). Comparison deltas in % modes are in
  // POINTS (pt), not a %-of-% growth number. Interactive rendering only —
  // the static (email) edition has no toggle and stays absolute.
  const [valueMode, setValueMode] = React.useState<"abs" | "pct" | "ctr">(
    "abs",
  );
  const interactive = !!onSelectStep;
  const pctOf = (
    v: number | null,
    base: number | null,
  ): number | null =>
    v == null || base == null || base <= 0 ? null : (v / base) * 100;

  if (!model.breakdownRows.length) return null;
  // Email and paper. Measured 2026-09-01 on a real weekly report: this table
  // wants 1046px and the mail column gives it 542, and `overflow-x: auto` has
  // nothing to scroll with in an inbox or on a printed page. The reader got
  // three of six columns — steps 3, 4 and 5 are add-to-cart, checkout and
  // purchase, so a conversion funnel arrived without its conversion.
  //
  // Narrowing the box is not enough: forcing the six columns into the width
  // with `table-layout: fixed` gives each ~90px, a cell carrying value,
  // comparison AND delta needs ~120, and the content overflows into its
  // neighbour — the table then reads "12,9199,292". A probe that counts which
  // columns fall inside the box scores that 6/6; it is not measuring what the
  // reader is graded on.
  //
  // So the static edition carries LESS per cell: the value and its delta, no
  // absolute comparison number. The funnel chart directly above already shows
  // the comparison per step, and the alternative on offer was three columns
  // the reader never sees at all. That one dropped fact takes the table from
  // 1046px to 542px, which is why no `table-layout` override is needed here —
  // measured, after trying the override first and finding it bought nothing
  // at the mail column width.
  //
  // Still open below ~480px, where 542px does not fit either: a phone reader
  // gets three of the six columns. Squeezing them in is what produced the
  // collisions above, so the answer there is content, not CSS.
  const staticEdition = !interactive;
  const cellNoWrap = staticEdition ? "normal" : "nowrap";
  const cellPad = "8px 12px";
  const rowCount = model.breakdownRows.length - 1; // excl. Total
  // The first column stays sticky so row labels survive the auto-scroll;
  // it needs an opaque background to cover the columns sliding beneath.
  const stickyFirstCol: React.CSSProperties = {
    position: "sticky",
    left: 0,
    zIndex: 1,
    background: "var(--verity-report-surface, var(--verity-color-surface, #fff))",
  };
  const pillStyle = (active: boolean): React.CSSProperties => ({
    border: `1px solid ${BORDER}`,
    background: active
      ? "var(--verity-color-surface, #fff)"
      : "var(--verity-color-surface-muted, #f9fafb)",
    color: active ? TEXT : TEXT_MUTED,
    padding: "3px 10px",
    cursor: "pointer",
    font: "inherit",
    fontSize: "var(--verity-font-size-xs, 12px)",
    fontWeight: active ? 600 : 400,
  });

  return (
    <div>
      {interactive && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 6,
          }}
        >
          <div
            role="group"
            aria-label="Table values: absolute, cumulative % or step CTR"
            style={{
              display: "inline-flex",
              borderRadius: "var(--verity-radius-full, 9999px)",
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => setValueMode("abs")}
              aria-pressed={valueMode === "abs"}
              style={{
                ...pillStyle(valueMode === "abs"),
                borderRadius: "9999px 0 0 9999px",
              }}
            >
              123
            </button>
            <button
              type="button"
              onClick={() => setValueMode("pct")}
              aria-pressed={valueMode === "pct"}
              style={{ ...pillStyle(valueMode === "pct"), borderLeft: "none" }}
            >
              %
            </button>
            <button
              type="button"
              onClick={() => setValueMode("ctr")}
              aria-pressed={valueMode === "ctr"}
              style={{
                ...pillStyle(valueMode === "ctr"),
                borderRadius: "0 9999px 9999px 0",
                borderLeft: "none",
              }}
            >
              CTR
            </button>
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        onMouseEnter={() => { pointerInTableRef.current = true; }}
        onMouseLeave={() => { pointerInTableRef.current = false; }}
        // Interactive: the table scrolls INSIDE the tile (sticky header)
        // so the funnel block above keeps its room — Arjen tightened the
        // tile height (28-08). Static/email keeps showing everything.
        style={
          interactive
            ? { overflow: "auto", maxHeight: 330 }
            : { overflowX: "auto" }
        }
      >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "var(--verity-font-size-sm, 13px)",
          color: TEXT,
        }}
      >
        <thead>
          <tr>
            <th
              scope="col"
              style={{
                textAlign: "left",
                padding: cellPad,
                borderBottom: `1px solid ${BORDER}`,
                color: TEXT_MUTED,
                fontWeight: 600,
                whiteSpace: cellNoWrap,
                ...stickyFirstCol,
                // Corner cell: sticky on BOTH axes now the body scrolls.
                top: 0,
                zIndex: 3,
              }}
            >
              {model.breakdownLabel}
            </th>
            {model.steps.map((s, i) => (
              <th
                key={s.key}
                scope="col"
                data-step-key={s.key}
                onClick={onSelectStep ? () => onSelectStep(s.key) : undefined}
                onMouseEnter={onHoverStep ? () => onHoverStep(s.key) : undefined}
                onMouseLeave={onHoverStep ? () => onHoverStep(null) : undefined}
                onKeyDown={
                  onSelectStep
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectStep(s.key);
                        }
                      }
                    : undefined
                }
                tabIndex={onSelectStep ? 0 : undefined}
                role={onSelectStep ? "button" : undefined}
                aria-label={onSelectStep ? `Select step ${s.label}` : undefined}
                style={{
                  textAlign: "right",
                  padding: cellPad,
                  borderBottom: `1px solid ${BORDER}`,
                  fontWeight: 600,
                  whiteSpace: cellNoWrap,
                  cursor: onSelectStep ? "pointer" : undefined,
                  // Sticky header row (the body scrolls beneath it) —
                  // needs an opaque background; the selection highlight
                  // doubles as that background when active.
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background:
                    s.key === selectedKey
                      ? ACCENT_SOFT
                      : "var(--verity-report-surface, var(--verity-color-surface, #fff))",
                }}
              >
                {i + 1}. {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.breakdownRows.map((row, rIdx) => {
            const isTotal = rIdx === 0;
            // Row-name click filters the whole tab on this value (Arjen,
            // 28-08). Total is an aggregate and "(not set)" is a bucket
            // label, not a stored value — neither is filterable.
            const filterable =
              !!onSelectBreakdownValue && !isTotal && row.name !== "(not set)";
            const isActiveFilter =
              !isTotal && activeBreakdownValue === row.name;
            // With a filter active: the selected row reads as SELECTED
            // (full-row accent background) and every other channel row
            // dims — the Total row stays at full strength as the anchor
            // (Arjen, 28-08).
            const dimRow =
              activeBreakdownValue != null && !isTotal && !isActiveFilter;
            return (
              <tr
                key={row.name}
                style={dimRow ? { opacity: 0.45 } : undefined}
              >
                <th
                  scope="row"
                  onClick={
                    filterable
                      ? () => onSelectBreakdownValue!(row.name)
                      : undefined
                  }
                  onKeyDown={
                    filterable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelectBreakdownValue!(row.name);
                          }
                        }
                      : undefined
                  }
                  tabIndex={filterable ? 0 : undefined}
                  role={filterable ? "button" : undefined}
                  aria-pressed={filterable ? isActiveFilter : undefined}
                  aria-label={
                    filterable
                      ? `Filter the page on ${row.name}`
                      : undefined
                  }
                  style={{
                    textAlign: "left",
                    padding: cellPad,
                    fontWeight: isTotal || isActiveFilter ? 700 : 400,
                    borderBottom:
                      rIdx < model.breakdownRows.length - 1
                        ? `1px solid var(--verity-color-border-subtle, rgba(0,0,0,0.06))`
                        : "none",
                    whiteSpace: cellNoWrap,
                    cursor: filterable ? "pointer" : undefined,
                    color: isActiveFilter
                      ? "var(--verity-color-accent, #3455D3)"
                      : undefined,
                    ...stickyFirstCol,
                    ...(isActiveFilter
                      ? { background: ACCENT_SOFT }
                      : {}),
                  }}
                >
                  {row.name}
                </th>
                {row.cells.map((cell, cIdx) => {
                  const stepKey = model.steps[cIdx]?.key;
                  // pct: share of the row's own step-1 value (cumulative);
                  // ctr: conversion from the PREVIOUS cell of the same row.
                  // Deltas in the % modes are in POINTS.
                  const base =
                    valueMode === "ctr" ? row.cells[cIdx - 1] : row.cells[0];
                  const inPctMode = valueMode !== "abs";
                  const pct =
                    valueMode === "ctr" && cIdx === 0
                      ? null
                      : pctOf(cell.value, base?.value ?? null);
                  const cmpPct =
                    valueMode === "ctr" && cIdx === 0
                      ? null
                      : pctOf(
                          cell.comparisonValue,
                          base?.comparisonValue ?? null,
                        );
                  const deltaPt =
                    pct != null && cmpPct != null ? pct - cmpPct : null;
                  const shownDelta = inPctMode ? deltaPt : cell.deltaPct;
                  return (
                    <td
                      key={stepKey ?? cIdx}
                      // The whole column CLICKS to select — but only the
                      // header hovers: a hover-preview on every cell made
                      // the selection flicker while reading the table
                      // (Arjen, 28-08).
                      onClick={
                        onSelectStep && stepKey
                          ? () => onSelectStep(stepKey)
                          : undefined
                      }
                      style={{
                        textAlign: "right",
                        padding: cellPad,
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: isTotal ? 700 : 400,
                        borderBottom:
                          rIdx < model.breakdownRows.length - 1
                            ? `1px solid var(--verity-color-border-subtle, rgba(0,0,0,0.06))`
                            : "none",
                        // Full-row selection for the active filter row wins
                        // over the step-column highlight.
                        background:
                          isActiveFilter || stepKey === selectedKey
                            ? ACCENT_SOFT
                            : undefined,
                        cursor: onSelectStep && stepKey ? "pointer" : undefined,
                      }}
                    >
                      {/* Two lines (Arjen, 28-08): actual + "vs. comp" side
                          by side, delta on the line below, right-aligned so
                          it sits under the comparison. The static edition
                          drops the "vs." span — see `staticEdition` above. */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "baseline",
                          gap: 8,
                          whiteSpace: cellNoWrap,
                        }}
                      >
                        <span>
                          {inPctMode
                            ? fmtPct(pct)
                            : fmtCount(cell.value, meta)}
                        </span>
                        {model.hasComparison && !staticEdition && (
                          <span
                            style={{
                              fontSize: "var(--verity-font-size-xs, 12px)",
                              color: TEXT_MUTED,
                            }}
                          >
                            vs.{" "}
                            {inPctMode
                              ? fmtPct(cmpPct)
                              : fmtCount(cell.comparisonValue, meta)}
                          </span>
                        )}
                      </div>
                      {model.hasComparison && (
                        <div
                          style={{
                            fontSize: "var(--verity-font-size-xs, 12px)",
                            whiteSpace: cellNoWrap,
                            color:
                              shownDelta == null
                                ? TEXT_MUTED
                                : shownDelta >= 0
                                  ? "var(--verity-color-positive, #0d9488)"
                                  : "var(--verity-color-negative, #f03e3e)",
                          }}
                        >
                          {shownDelta == null
                            ? "—"
                            : `${shownDelta >= 0 ? "↑" : "↓"} ${Math.abs(shownDelta).toFixed(1)}${inPctMode ? " pt" : "%"}`}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
        {rowCount >= 10 && (
          <div
            style={{
              padding: "6px 12px",
              fontSize: "var(--verity-font-size-xs, 12px)",
              color: TEXT_MUTED,
            }}
          >
            Top {rowCount} shown
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel (ECharts bar + step-CTR line)
// ---------------------------------------------------------------------------

function DetailPanel({
  data,
  stepKey,
  meta,
  comparisonName,
}: {
  data: FunnelExplorerData;
  stepKey: string;
  meta?: ChartMeta;
  comparisonName?: string;
}): React.ReactElement | null {
  const detail = React.useMemo(
    () => buildFunnelDetailModel(data, stepKey),
    [data, stepKey],
  );

  const paletteBar = React.useContext(DetailBarContext);
  const { Chart } = useChartRenderer();
  const option = React.useMemo(() => {
    if (!detail || !detail.dates.length) return null;
    const barColor = paletteBar ?? resolveCssVar("--verity-chart-bar-fill", "#3455D3");
    const lineColor = resolveCssVar("--verity-color-positive", "#0d9488");
    // Same resting grey as the funnel's comparison bars — one comparison
    // colour story across the tile.
    const cmpColor = resolveCssVar("--verity-gray-4", "#9CA3AF");
    const rateName = detail.nextLabel ? `→ ${detail.nextLabel}` : null;
    const cmpName = comparisonName ?? "Comparison";

    const series: object[] = [
      // Actual FIRST: series order is bar order within a group, and the
      // actual bar belongs on the left (Arjen, 28-08). Positional
      // day-shift alignment for the comparison (model docs).
      {
        name: detail.label,
        type: "bar",
        data: detail.counts,
        barMaxWidth: 24,
        itemStyle: { color: barColor, borderRadius: [3, 3, 0, 0] },
        yAxisIndex: 0,
        z: 2,
      },
      ...(detail.comparisonCounts
        ? [
            {
              name: cmpName,
              type: "bar",
              data: detail.comparisonCounts,
              barMaxWidth: 24,
              itemStyle: { color: cmpColor, borderRadius: [3, 3, 0, 0] },
              yAxisIndex: 0,
              z: 1,
            },
          ]
        : []),
    ];
    const yAxis: object[] = [
      {
        type: "value",
        axisLabel: valueAxisLabel(meta),
        splitLine: { lineStyle: { color: "var(--verity-chart-grid-stroke, rgba(0,0,0,0.08))" } },
      },
    ];
    if (detail.stepRates && rateName) {
      series.push({
        name: rateName,
        type: "line",
        data: detail.stepRates,
        yAxisIndex: 1,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: lineColor, cap: "round", join: "round" },
        itemStyle: { color: lineColor },
        connectNulls: false,
        z: 3,
        // The axis tooltip's meta formatter is count-shaped; the rate line
        // is a percentage — 2 decimals with the % sign (Arjen, 28-08).
        tooltip: {
          valueFormatter: (v: unknown) =>
            typeof v === "number" ? `${v.toFixed(2)}%` : "—",
        },
      });
      if (detail.comparisonStepRates) {
        // Comparison CTR: dotted and slightly thinner than the main line
        // (Arjen, 28-08) — same green, so it reads as "the same rate,
        // other window" next to the grey comparison bars.
        series.push({
          name: `${rateName} (${cmpName})`,
          type: "line",
          data: detail.comparisonStepRates,
          yAxisIndex: 1,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 1.5, color: lineColor, type: "dashed" },
          itemStyle: { color: lineColor },
          connectNulls: false,
          z: 3,
          tooltip: {
            valueFormatter: (v: unknown) =>
              typeof v === "number" ? `${v.toFixed(2)}%` : "—",
          },
        });
      }
      yAxis.push({
        type: "value",
        axisLabel: {
          ...AXIS_LABEL_DEFAULT,
          formatter: (v: number) => `${Math.round(v)}%`,
        },
        splitLine: { show: false },
      });
    }
    return withBaseOptions({
      grid: { left: 48, right: detail.stepRates ? 44 : 16, top: 12, bottom: 44 },
      // Default axis tooltip: the comparison series carries its period
      // name ("Previous period"), which is what disambiguates the grey
      // bar — its own calendar date would need a custom formatter for
      // marginal value.
      tooltip: { ...tooltipWithMeta(meta), trigger: "axis" },
      legend: {
        bottom: 0,
        icon: "circle",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: "var(--verity-color-text-muted, #6b7280)", fontSize: 11 },
      },
      xAxis: {
        type: "category",
        data: detail.dates,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          ...AXIS_LABEL_DEFAULT,
          formatter: (d: string) => d.slice(5),
          hideOverlap: true,
        },
      },
      yAxis,
      series,
    });
  }, [detail, meta, paletteBar]);

  if (!detail || !option) return null;

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <span
          style={{
            fontSize: "var(--verity-font-size-sm, 13px)",
            fontWeight: 600,
            color: TEXT,
          }}
        >
          {detail.label} per day
        </span>
        {detail.nextLabel && (
          <span style={{ fontSize: "var(--verity-font-size-sm, 13px)", color: TEXT_MUTED }}>
            {" "}
            — line: conversion to {detail.nextLabel}
          </span>
        )}
      </div>
      {/* Explicit height, not minHeight: the EChart child sizes itself with
       * height:100%, which resolves to 0 against a parent whose `height`
       * is auto — the chart then never initialises. */}
      <div style={{ height: 280 }}>
        <Chart option={option} heightPx={280} ariaLabel={`${detail.label} per day`} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FunnelExplorerChart({
  data,
  meta,
  ariaLabel,
  comparisonName = "Previous period",
  interactive,
  onSelectBreakdownValue,
  activeBreakdownValue,
  palette,
}: FunnelExplorerChartProps): React.ReactElement {
  const paletteFirst = palette && palette.length > 0 ? palette[0] : null;
  const funnelFill = paletteFirst ?? FUNNEL_FILL;
  // The static edition says so through the renderer context; the sandbox's
  // e-mail runtime through its global flag.
  const renderer = useChartRenderer();
  const isStatic =
    interactive === undefined
      ? renderer.isStatic
        || (globalThis as { __verityChartRenderer?: string }).__verityChartRenderer === "svg"
      : !interactive;

  const hasComparisonData = !!data?.comparison?.steps?.length;
  const [showComparison, setShowComparison] = React.useState(true);
  const [hoverSeries, setHoverSeries] = React.useState<HoverSeries>(null);

  // The toggle strips comparison at the DATA level, so everything derived
  // (bars, wedges, legend, table vs. rows, axis max) follows in one place.
  const dataView = React.useMemo(() => {
    const d = data ?? { steps: [] };
    if (showComparison || !d.comparison) return d;
    return { ...d, comparison: undefined };
  }, [data, showComparison]);

  const model = React.useMemo(() => buildFunnelExplorerModel(dataView), [dataView]);

  const firstSelectable =
    model.steps.find((s) => !s.missing)?.key ?? null;
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  // Hovering a step column PREVIEWS the selection: the table highlight and
  // the detail panel follow the hover as if it were clicked, and fall back
  // to the clicked selection when the pointer leaves.
  const [hoverStepKey, setHoverStepKey] = React.useState<string | null>(null);
  const readable = (key: string | null): string | null =>
    key != null && model.steps.some((s) => s.key === key && !s.missing)
      ? key
      : null;
  // Key-based with fallback to the first readable step — never an index,
  // which points past the end after a period/step change.
  const clickedKey = readable(selectedKey);
  const effectiveKey = readable(hoverStepKey) ?? clickedKey ?? firstSelectable;

  const allUnreadable = model.steps.every((s) => s.missing);
  if (!model.steps.length || allUnreadable) {
    return <EmptyChart ariaLabel={ariaLabel ?? "Funnel"} message="No funnel data available" />;
  }

  // dataView, not data: the prop may be undefined (dataView already guards
  // it) and the comparison toggle strips at the dataView level anyway.
  const showDetail = !isStatic && !!dataView.series && effectiveKey != null;
  // Header grows a line when the cumulative comparison row is shown.
  const headerH = model.hasComparison ? 76 : 58;
  const hoverIdx =
    hoverStepKey != null
      ? model.steps.findIndex((s) => s.key === hoverStepKey)
      : -1;

  const tree = (
    <div
      role="group"
      aria-label={ariaLabel ?? "Funnel"}
      style={{
        fontFamily: "var(--verity-font-family-sans, system-ui, sans-serif)",
        color: TEXT,
      }}
    >
      {/* Comparison toggle — only when the payload carries a comparison */}
      {hasComparisonData && !isStatic && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <button
            type="button"
            onClick={() => setShowComparison((v) => !v)}
            aria-pressed={showComparison}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              border: `1px solid ${BORDER}`,
              background: showComparison
                ? "var(--verity-color-surface, #fff)"
                : "var(--verity-color-surface-muted, #f9fafb)",
              color: showComparison ? TEXT : TEXT_MUTED,
              borderRadius: "var(--verity-radius-full, 9999px)",
              padding: "4px 12px",
              cursor: "pointer",
              font: "inherit",
              fontSize: "var(--verity-font-size-xs, 12px)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: "var(--verity-gray-4, #9CA3AF)",
                opacity: showComparison ? 1 : 0.35,
                transition: "opacity 130ms ease",
              }}
            />
            {comparisonName}
          </button>
        </div>
      )}

      {/* Funnel strip + abandonment strip share one relative wrapper so the
       * dashed step separators can be drawn ONCE per boundary as a single
       * continuous overlay line — borders on the columns and cells left
       * seams at the strip joint and stopped before the last cell. The
       * overlay sits first in DOM (behind bars and wedges), faintly
       * shimmering through the translucent fills. No y-axis: the count
       * labels on the bars carry the scale (Arjen, ronde 10). */}
      {/* isolation creates a local stacking context so the overlay's
       * zIndex -1 stays INSIDE this wrapper: without it the band would
       * paint over the strips' static text (positioned elements beat
       * in-flow text), or dive behind an ancestor's background. */}
      <div style={{ position: "relative", isolation: "isolate" }}>
        <div
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: -1 }}
        >
          {model.steps.slice(1).map((s, i) => (
            <div
              key={s.key}
              style={{
                position: "absolute",
                left: `${((i + 1) / model.steps.length) * 100}%`,
                top: 0,
                bottom: 0,
                borderLeft: STEP_RULE,
              }}
            />
          ))}
          {/* Lane-hover band: lives HERE (not on the column button) so the
           * wash spans header, chart AND the abandonment row in one piece. */}
          {!isStatic && hoverIdx >= 0 && (
            <div
              style={{
                position: "absolute",
                left: `${(hoverIdx / model.steps.length) * 100}%`,
                width: `${100 / model.steps.length}%`,
                top: 0,
                bottom: 0,
                background: ACCENT_SOFT,
              }}
            />
          )}
        </div>
        <div style={{ display: "flex" }}>
          {model.steps.map((s, i) => (
            <StepColumn
              key={s.key}
              step={s}
              nextStep={model.steps[i + 1] ?? null}
              index={i}
              maxValue={model.maxValue}
              meta={meta}
              selected={!isStatic && s.key === (clickedKey ?? firstSelectable)}
              // Dim only after an EXPLICIT click — the default state (detail
              // panel follows step 1) keeps every step at full strength.
              dimmed={!isStatic && clickedKey != null && s.key !== clickedKey}
              onHoverStep={setHoverStepKey}
              interactive={!isStatic}
              comparisonName={model.hasComparison ? comparisonName : undefined}
              onSelect={setSelectedKey}
              hoverSeries={isStatic ? null : hoverSeries}
              onHoverSeries={setHoverSeries}
              headerHeight={headerH}
            />
          ))}
        </div>

        {/* Abandonment strip */}
        <div style={{ display: "flex" }}>
          {model.steps.map((s) => (
            <AbandonmentCell
              key={s.key}
              step={s}
              meta={meta}
              onHoverStep={isStatic ? undefined : setHoverStepKey}
            />
          ))}
        </div>
      </div>

      {/* Comparison legend: paired-bar swatches */}
      {model.hasComparison && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "8px 0 0 0",
            fontSize: "var(--verity-font-size-xs, 12px)",
            color: TEXT_MUTED,
          }}
        >
          <span
            onMouseEnter={isStatic ? undefined : () => setHoverSeries("actual")}
            onMouseLeave={isStatic ? undefined : () => setHoverSeries(null)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              opacity: hoverSeries === "comparison" ? 0.4 : 1,
              transition: "opacity 130ms ease",
            }}
          >
            <span
              aria-hidden="true"
              style={{ width: 10, height: 10, borderRadius: 2, background: funnelFill }}
            />
            Actual
          </span>
          <span
            onMouseEnter={isStatic ? undefined : () => setHoverSeries("comparison")}
            onMouseLeave={isStatic ? undefined : () => setHoverSeries(null)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              opacity: hoverSeries === "actual" ? 0.4 : 1,
              transition: "opacity 130ms ease",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background:
                  hoverSeries === "comparison"
                    ? "var(--verity-gray-6, #4B5563)"
                    : "var(--verity-gray-4, #9CA3AF)",
                transition: "background 130ms ease",
              }}
            />
            {comparisonName}
          </span>
        </div>
      )}

      {/* Breakdown table + detail panel */}
      {(model.breakdownRows.length > 0 || showDetail) && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginTop: 20,
            flexWrap: "wrap",
            alignItems: "stretch",
          }}
        >
          {model.breakdownRows.length > 0 && (
            <div style={{ flex: "3 1 420px", minWidth: 0 }}>
              <BreakdownTable
                model={model}
                meta={meta}
                selectedKey={isStatic ? null : effectiveKey}
                onSelectStep={isStatic ? undefined : setSelectedKey}
                onHoverStep={isStatic ? undefined : setHoverStepKey}
                onSelectBreakdownValue={
                  isStatic ? undefined : onSelectBreakdownValue
                }
                activeBreakdownValue={isStatic ? null : activeBreakdownValue}
              />
            </div>
          )}
          {showDetail && effectiveKey && (
            <div style={{ flex: "2 1 320px", minWidth: 0 }}>
              {/* dataView, not data: the comparison toggle strips at the
                  dataView level, and the panel must follow it. */}
              <DetailPanel
                data={dataView}
                stepKey={effectiveKey}
                meta={meta}
                comparisonName={model.hasComparison ? comparisonName : undefined}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
  // No provider without a palette: the default context values ARE today's
  // tokens, so an absent palette leaves the tree exactly as it was.
  if (!paletteFirst) return tree;
  return (
    <FunnelFillContext.Provider value={paletteFirst}>
      <DetailBarContext.Provider value={paletteFirst}>{tree}</DetailBarContext.Provider>
    </FunnelFillContext.Provider>
  );
}
